import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientPurposeSelector, PurposeOption } from '../components/patient/PatientPurposeSelector';
import { HeartIcon } from '@heroicons/react/24/outline';

export const PatientWelcomePage: React.FC = () => {
  const navigate = useNavigate();

  const handlePurposeSelect = (purposeId: PurposeOption['id']) => {
    switch (purposeId) {
      case 'services':
        navigate('/onecare/patients/services');
        break;
      case 'medical-assistance':
        navigate('/onecare/patients/medical-assistance');
        break;
      case 'encounter':
        navigate('/onecare/patients/encounter');
        break;
      case 'track-feedback':
        navigate('/onecare/patients/track');
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <button 
              onClick={() => navigate('/onecare/')}
              className="flex items-center group"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <HeartIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  Aimepic OneCare
                </h1>
                <p className="text-sm text-gray-800">Patient Portal</p>
              </div>
            </button>
            
            <nav className="flex space-x-8">
              <button 
                onClick={() => navigate('/onecare/patients/track')}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Track Feedback
              </button>
              <button 
                onClick={() => navigate('/onecare/')}
                className="text-gray-900 hover:text-blue-600 transition-colors"
              >
                ← Back to Home
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Welcome to Our 
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Patient Portal
            </span>
          </h1>
          <p className="text-xl text-gray-900 max-w-3xl mx-auto">
            We're here to provide you with the best healthcare experience. 
            Let us know how we can assist you today.
          </p>
        </div>

        <PatientPurposeSelector 
          onSelect={handlePurposeSelect}
          className="max-w-5xl mx-auto"
        />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <HeartIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Help & Support
              </button>
              <span className="text-xs">
                © 2025 Aimepic Digital Healthcare Services
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PatientWelcomePage;